import { type i18n as I18n } from "i18next";

// Define supported languages and locales as constants
const SUPPORTED_LANGUAGES = ["pt", "en", "de", "hi"] as const;
const SUPPORTED_LOCALES = ["pt-BR", "en-US", "de-DE", "hi-IN"] as const;

// Create type-safe string literals
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

/**
 * Extracts the language code from a locale string
 * @param locale - The locale string to process
 * @returns The language code or null if not supported
 */
export function getLocaleLanguage(locale: string): SupportedLanguage | null {
  if (locale?.startsWith("pt")) {
    return "pt";
  }

  if (locale?.startsWith("en")) {
    return "en";
  }

  if (locale?.startsWith("de")) {
    return "de";
  }

  if (locale?.startsWith("hi")) {
    return "hi";
  }

  return null;
}

/**
 * Converts a language code to a full locale
 * @param language - The language code to convert
 * @returns The corresponding locale
 */
export function getForcedLanguageLocale(language?: string): SupportedLocale {
  if (language?.startsWith("pt")) {
    return "pt-BR";
  }

  if (language?.startsWith("de")) {
    return "de-DE";
  }

  if (language?.startsWith("hi")) {
    return "hi-IN";
  }

  return "en-US";
}

/**
 * Gets the current language locale from i18n
 * @param i18n - The i18n instance
 * @returns The current locale
 */
export function getLanguageLocale(i18n: I18n): SupportedLocale {
  return getForcedLanguageLocale(i18n.resolvedLanguage);
}
